// src/components/module/ventas/CreditDetailModal.tsx
// Modal de detalle de crédito — muestra cuotas y permite pagar
import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  IconButton,
  Stack,
  Fade,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon, CreditCard, CalendarToday, ReceiptLong,
  AccountBalanceWallet, CheckCircleOutline, Schedule, ErrorOutline,
  Payment, Today,
} from "@mui/icons-material";
import { CreditoApi, CreditoVenta, CuotaCredito, RespuestaPagoCuota } from "../../../services/api/creditoApi";
import { formatMoney, formatDate } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { PagoCuotaModal } from "./PagoCuotaModal";

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    purple: "#6940A5", purpleBg: "#EAE4F2",
    hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    purple: "#9A6DD7", purpleBg: "#2A2040",
    hover: "rgba(255,255,255,0.04)",
  },
};

interface CreditDetailModalProps {
  ventaId: number;
  open: boolean;
  onClose: () => void;
  onCuotaPagada?: () => void;
}

const getEstadoChip = (estado: string, dark: boolean) => {
  const p = dark ? P.dark : P.light;
  const s = estado.toLowerCase();
  const map: Record<string, { bg: string; color: string; label: string; icon: React.ReactElement }> = {
    pagada: { bg: p.successBg, color: p.success, label: "Pagada", icon: <CheckCircleOutline sx={{ fontSize: 14 }} /> },
    pendiente: { bg: p.warningBg, color: p.warning, label: "Pendiente", icon: <Schedule sx={{ fontSize: 14 }} /> },
    vencida: { bg: p.errorBg, color: p.error, label: "Vencida", icon: <ErrorOutline sx={{ fontSize: 14 }} /> },
  };
  const cfg = map[s] || { bg: p.bgT, color: p.textS, label: estado, icon: null };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.35, borderRadius: "6px", bgcolor: cfg.bg, color: cfg.color, fontSize: "0.75rem", fontWeight: 600 }}>
      {cfg.icon}
      {cfg.label}
    </Box>
  );
};

export const CreditDetailModal: React.FC<CreditDetailModalProps> = ({ ventaId, open, onClose, onCuotaPagada }) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const [credito, setCredito] = useState<CreditoVenta | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState<CuotaCredito | null>(null);

  useEffect(() => {
    if (open && ventaId) loadCredito();
  }, [open, ventaId]);

  const loadCredito = async () => {
    try {
      setLoading(true);
      const creditos = await CreditoApi.listar();
      const encontrado = creditos.find((c) => c.IdVenta === ventaId);
      if (encontrado) {
        // Recargar con detalle de cuotas
        const detalle = await CreditoApi.obtener(encontrado.Id);
        setCredito(detalle);
      } else {
        setCredito(null);
      }
    } catch {
      toast.error("No se pudo cargar el crédito");
    } finally {
      setLoading(false);
    }
  };

  const handlePagarCuota = (cuota: CuotaCredito) => {
    setCuotaSeleccionada(cuota);
    setPagoModalOpen(true);
  };

  const handlePagoExitoso = async (respuesta: RespuestaPagoCuota) => {
    setPagoModalOpen(false);
    setCuotaSeleccionada(null);
    await loadCredito();
    onCuotaPagada?.();
    Swal.fire({
      icon: "success",
      title: "Abono registrado",
      html: respuesta.resumen.length > 1
        ? `<b>${respuesta.resumen.length} cuotas afectadas</b><br/>${respuesta.resumen.map(r => `Cuota #${r.numeroCuota}: ${formatMoney(r.valorAplicado)}`).join("<br/>")}`
        : `Cuota #${respuesta.resumen[0]?.numeroCuota} pagada con ${formatMoney(respuesta.resumen[0]?.valorAplicado)}`,
      timer: 4000,
      showConfirmButton: false,
    });
  };

  const progreso = credito ? Math.round(((credito.MontoTotal - credito.Saldo) / credito.MontoTotal) * 100) : 0;

  const thStyle = {
    fontSize: "0.72rem", fontWeight: 600, color: p.textT,
    textTransform: "uppercase" as const, letterSpacing: "0.04em",
    borderBottom: `1px solid ${p.border}`, py: 1, bgcolor: p.bgS,
  };
  const tdStyle = { borderBottom: `1px solid ${p.border}`, py: 1.2, fontSize: "0.84rem" };

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  // Determinar estado real de cuotas pendientes
  const getEstadoReal = (cuota: CuotaCredito): string => {
    if (cuota.Estado === "pagada") return "pagada";
    const venc = new Date(`${cuota.FechaVencimiento}T00:00:00`);
    return venc < hoy ? "vencida" : "pendiente";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
        sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" },
      }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 42, height: 42, borderRadius: "10px", bgcolor: p.purpleBg, display: "flex", alignItems: "center", justifyContent: "center", color: darkMode ? P.dark.purple : P.light.purple }}>
              <CreditCard sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>
                Plan de Crédito
              </Typography>
              {credito && (
                <Typography sx={{ fontSize: "0.78rem", color: p.textT, mt: 0.2 }}>
                  {credito.NumeroCuotas} cuotas · {credito.Activo ? "Activo" : "Finalizado"}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ width: 34, height: 34, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={36} sx={{ color: p.accent }} />
            </Box>
          ) : !credito ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CreditCard sx={{ fontSize: 48, color: p.textT, mb: 1.5 }} />
              <Typography sx={{ fontSize: "0.9rem", color: p.textT }}>No se encontró un crédito para esta venta</Typography>
            </Box>
          ) : (
            <Fade in timeout={300}>
              <Box>
                {/* Info cards */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {/* Datos del crédito */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Datos del Crédito</Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Monto Total</Typography>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(credito.MontoTotal)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Saldo Pendiente</Typography>
                          <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: p.error, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(credito.Saldo)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Valor por Cuota</Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(credito.ValorCuota)}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Progreso */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Progreso de Pago</Typography>
                      <Stack spacing={2.5}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography sx={{ fontSize: "2.2rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{progreso}%</Typography>
                          <Typography sx={{ fontSize: "0.78rem", color: p.textT, mt: 0.3 }}>pagado</Typography>
                        </Box>
                        <Box sx={{ width: "100%", height: 8, borderRadius: "4px", bgcolor: p.bgT, overflow: "hidden" }}>
                          <Box sx={{ width: `${progreso}%`, height: "100%", borderRadius: "4px", bgcolor: p.accent, transition: "width 0.4s ease" }} />
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "0.75rem", color: p.textS }}>
                            {credito.cuotas?.filter((c) => c.Estado === "pagada").length ?? 0} pagadas
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: p.textS }}>
                            {credito.NumeroCuotas} total
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Fechas */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Fechas Clave</Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Primera Cuota</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarToday sx={{ fontSize: 14, color: p.textS }} />
                            <Typography sx={{ fontSize: "0.88rem", color: p.text }}>{formatDate(credito.FechaPrimeraCuota)}</Typography>
                          </Stack>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Intervalo</Typography>
                          <Typography sx={{ fontSize: "0.88rem", color: p.text }}>
                            {credito.IntervaloDias === 7 ? "Semanal" : credito.IntervaloDias === 15 ? "Quincenal" : credito.IntervaloDias === 30 ? "Mensual" : `Cada ${credito.IntervaloDias} días`}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.3 }}>Fecha Registro</Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Today sx={{ fontSize: 14, color: p.textS }} />
                            <Typography sx={{ fontSize: "0.88rem", color: p.text }}>{formatDate(credito.FechaRegistro)}</Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Tabla de cuotas */}
                <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${p.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AccountBalanceWallet sx={{ fontSize: 16, color: darkMode ? P.dark.purple : P.light.purple }} />
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text }}>Cuotas ({credito.cuotas?.length ?? 0})</Typography>
                    </Stack>
                    <Tooltip title="Actualizar">
                      <IconButton onClick={loadCredito} size="small" sx={{ color: p.textS, bgcolor: p.bgT, borderRadius: "7px", width: 30, height: 30, "& svg": { fontSize: 15 } }}>
                        <Schedule sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["#", "Vencimiento", "Valor", "Pagado", "Fecha Pago", "Estado", ""].map((h, i) => (
                            <TableCell key={h} align={i >= 2 && i <= 4 ? "right" : i === 5 ? "center" : i === 6 ? "center" : "left"} sx={thStyle}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {credito.cuotas?.map((cuota) => {
                          const estadoReal = getEstadoReal(cuota);
                          const isPendiente = estadoReal === "pendiente" || estadoReal === "vencida";
                          return (
                            <TableRow key={cuota.Id} sx={{ "&:hover": { bgcolor: p.hover }, transition: "background 0.1s" }}>
                              <TableCell sx={{ ...tdStyle, fontWeight: 700, color: p.text, fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.82rem" }}>
                                {cuota.NumeroCuota}
                              </TableCell>
                              <TableCell sx={{ ...tdStyle, color: p.textS }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <CalendarToday sx={{ fontSize: 13, color: p.textT }} />
                                  <Typography sx={{ fontSize: "0.84rem" }}>{formatDate(cuota.FechaVencimiento)}</Typography>
                                  {estadoReal === "vencida" && (
                                    <Chip label="Vencida" size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: p.errorBg, color: p.error, fontWeight: 600 }} />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right" sx={{ ...tdStyle, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", color: p.text }}>
                                {formatMoney(cuota.ValorCuota)}
                              </TableCell>
                              <TableCell align="right" sx={{ ...tdStyle, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", color: cuota.ValorPagado > 0 ? p.success : p.textT }}>
                                {cuota.ValorPagado > 0 ? formatMoney(cuota.ValorPagado) : "—"}
                              </TableCell>
                              <TableCell align="right" sx={{ ...tdStyle, color: p.textS, fontSize: "0.82rem" }}>
                                {cuota.FechaPago ? formatDate(cuota.FechaPago) : "—"}
                              </TableCell>
                              <TableCell align="center" sx={tdStyle}>
                                {getEstadoChip(estadoReal, darkMode)}
                              </TableCell>
                              <TableCell align="center" sx={tdStyle}>
                                {isPendiente ? (
                                  <Tooltip title="Pagar cuota" arrow>
                                    <span>
                                      <IconButton
                                        onClick={() => handlePagarCuota(cuota)}
                                        size="small"
                                        sx={{
                                          width: 28, height: 28, borderRadius: "7px",
                                          color: p.accent, bgcolor: p.accentBg,
                                          "& svg": { fontSize: 15 },
                                          "&:hover": { bgcolor: darkMode ? "#1C4466" : "#D4E4F7" },
                                        }}
                                      >
                                        <Payment />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <CheckCircleOutline sx={{ fontSize: 16, color: p.success }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            </Fade>
          )}
        </DialogContent>

        {/* Modal de pago */}
        {cuotaSeleccionada && credito && (
          <PagoCuotaModal
            open={pagoModalOpen}
            onClose={() => { setPagoModalOpen(false); setCuotaSeleccionada(null); }}
            cuota={cuotaSeleccionada}
            todasCuotas={credito.cuotas || []}
            onPagoExitoso={handlePagoExitoso}
            creditoNombre={`Venta #${credito.IdVenta}`}
          />
        )}
      </Dialog>
  );
};

export default CreditDetailModal;
